import { useEffect, useState } from "react";
import axios from "axios";
import { useAuth } from "@/context/AuthContext";
import { Plus, Pencil, Trash2, X, Key } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminUtilisateurs() {
  const { authHeaders, user: currentUser } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editUser, setEditUser] = useState(null);
  const [showAdd, setShowAdd] = useState(false);
  const [deleting, setDeleting] = useState(null);

  const fetchUsers = () => {
    setLoading(true);
    axios.get(`${API}/users`, { headers: authHeaders })
      .then(r => setUsers(r.data)).finally(() => setLoading(false));
  };

  useEffect(() => { fetchUsers(); }, []);

  const deleteUser = async (id) => {
    await axios.delete(`${API}/users/${id}`, { headers: authHeaders });
    setDeleting(null);
    fetchUsers();
  };

  const formatDate = (d) => {
    if (!d) return "";
    try { return new Date(d).toLocaleDateString("fr-FR"); } catch { return d; }
  };

  const getInitial = (nom) => nom?.[0]?.toUpperCase() || "?";
  const getColor = (nom) => {
    const colors = ["bg-amber-500", "bg-green-500", "bg-blue-500", "bg-purple-500", "bg-red-500", "bg-pink-500"];
    return colors[(nom?.charCodeAt(0) || 0) % colors.length];
  };

  return (
    <div className="p-8">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-2xl font-bold text-gray-900" data-testid="users-title">Utilisateurs</h1>
          <p className="text-gray-500 text-sm mt-1">Gérez les accès à l'administration</p>
        </div>
        <button onClick={() => setShowAdd(true)} data-testid="add-user-btn"
          className="flex items-center gap-2 px-5 py-2.5 bg-[#1B2E4B] text-white text-sm font-semibold hover:bg-[#162440] transition-colors">
          <Plus size={16} /> AJOUTER UN UTILISATEUR
        </button>
      </div>

      <div className="bg-white shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="users-table">
            <thead>
              <tr className="border-b border-gray-100 text-xs text-gray-400 font-semibold tracking-wider uppercase bg-gray-50">
                {["NOM", "EMAIL", "RÔLE", "DATE DE CRÉATION", "ACTIONS"].map(h => (
                  <th key={h} className="px-6 py-4 text-left">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={5} className="text-center py-12 text-gray-400">Chargement...</td></tr>
              ) : users.map(u => (
                <tr key={u.id} className="border-b border-gray-50 hover:bg-gray-50 transition-colors">
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-3">
                      <div className={`w-8 h-8 rounded-full ${getColor(u.nom)} flex items-center justify-center text-white text-sm font-bold`}>
                        {getInitial(u.nom)}
                      </div>
                      <div>
                        <p className="font-semibold text-gray-900">{u.nom}</p>
                        {currentUser?.id === u.id && (
                          <span className="text-xs bg-gray-100 text-gray-500 px-2 py-0.5 rounded-full">Vous</span>
                        )}
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 text-gray-600">{u.email}</td>
                  <td className="px-6 py-4">
                    <span className="px-2 py-1 bg-purple-100 text-purple-700 text-xs font-semibold rounded-sm">
                      {u.role === "admin" ? "Admin" : u.role}
                    </span>
                  </td>
                  <td className="px-6 py-4 text-gray-500">{formatDate(u.created_at)}</td>
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-2">
                      <button onClick={() => setEditUser(u)} data-testid={`edit-user-${u.id}`}
                        className="text-gray-400 hover:text-gray-700 transition-colors"><Pencil size={15} /></button>
                      <button onClick={() => setEditUser({ ...u, changePassword: true })} data-testid={`pwd-user-${u.id}`}
                        className="text-gray-400 hover:text-blue-500 transition-colors"><Key size={15} /></button>
                      {currentUser?.id !== u.id && (
                        <button onClick={() => setDeleting(u.id)} data-testid={`delete-user-${u.id}`}
                          className="text-gray-400 hover:text-red-500 transition-colors"><Trash2 size={15} /></button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {(showAdd || editUser) && (
        <UserModal
          user={editUser}
          authHeaders={authHeaders}
          onClose={() => { setShowAdd(false); setEditUser(null); }}
          onSaved={() => { setShowAdd(false); setEditUser(null); fetchUsers(); }}
        />
      )}

      {deleting && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white p-6 max-w-sm w-full shadow-2xl">
            <h3 className="font-bold text-gray-900 mb-3">Supprimer cet utilisateur ?</h3>
            <p className="text-gray-500 text-sm mb-6">Cette action est irréversible.</p>
            <div className="flex gap-3 justify-end">
              <button onClick={() => setDeleting(null)} className="px-5 py-2 border border-gray-200 text-sm font-semibold">Annuler</button>
              <button onClick={() => deleteUser(deleting)} data-testid="confirm-delete-user"
                className="px-5 py-2 bg-red-500 text-white text-sm font-semibold hover:bg-red-600">Supprimer</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function UserModal({ user, authHeaders, onClose, onSaved }) {
  const isEdit = !!user && !user?.changePassword;
  const isPwd = user?.changePassword;

  const [form, setForm] = useState({
    nom: user?.nom || "",
    email: user?.email || "",
    password: "",
    role: user?.role || "admin",
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true); setError("");
    try {
      const data = isPwd ? { password: form.password } : form;
      if (isEdit || isPwd) {
        await axios.put(`${API}/users/${user.id}`, data, { headers: authHeaders });
      } else {
        await axios.post(`${API}/users`, form, { headers: authHeaders });
      }
      onSaved();
    } catch (err) {
      setError(err.response?.data?.detail || "Erreur");
    } finally { setLoading(false); }
  };

  const title = isPwd ? "Changer le mot de passe" : isEdit ? "Modifier l'utilisateur" : "Ajouter un utilisateur";

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-white w-full max-w-md shadow-2xl">
        <div className="flex items-center justify-between p-6 border-b border-gray-100">
          <h2 className="font-bold text-gray-900">{title}</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600"><X size={18} /></button>
        </div>
        <form onSubmit={submit} className="p-6 space-y-4">
          {error && <p className="text-red-500 text-sm bg-red-50 p-3">{error}</p>}
          {!isPwd && (
            <>
              <div>
                <label className="block text-xs font-semibold text-gray-600 mb-1 uppercase tracking-wider">Nom *</label>
                <input type="text" value={form.nom} onChange={e => set("nom", e.target.value)} required
                  data-testid="user-nom"
                  className="w-full border border-gray-200 px-3 py-2.5 text-sm focus:outline-none focus:border-[#D4A843]" />
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-600 mb-1 uppercase tracking-wider">Email *</label>
                <input type="email" value={form.email} onChange={e => set("email", e.target.value)} required
                  data-testid="user-email"
                  className="w-full border border-gray-200 px-3 py-2.5 text-sm focus:outline-none focus:border-[#D4A843]" />
              </div>
            </>
          )}
          {(!isEdit || isPwd) && (
            <div>
              <label className="block text-xs font-semibold text-gray-600 mb-1 uppercase tracking-wider">Mot de passe *</label>
              <input type="password" value={form.password} onChange={e => set("password", e.target.value)} required
                data-testid="user-password"
                className="w-full border border-gray-200 px-3 py-2.5 text-sm focus:outline-none focus:border-[#D4A843]" />
            </div>
          )}
          {!isPwd && (
            <div>
              <label className="block text-xs font-semibold text-gray-600 mb-1 uppercase tracking-wider">Rôle</label>
              <select value={form.role} onChange={e => set("role", e.target.value)}
                data-testid="user-role"
                className="w-full border border-gray-200 px-3 py-2.5 text-sm focus:outline-none focus:border-[#D4A843]">
                <option value="admin">Admin</option>
                <option value="manager">Manager</option>
              </select>
            </div>
          )}
          <div className="flex gap-3 pt-2 justify-end border-t border-gray-100">
            <button type="button" onClick={onClose} className="px-5 py-2 border border-gray-200 text-sm font-semibold hover:bg-gray-50">Annuler</button>
            <button type="submit" disabled={loading} data-testid="save-user-btn"
              className="px-6 py-2 bg-[#1B2E4B] text-white text-sm font-semibold hover:bg-[#162440] disabled:opacity-60">
              {loading ? "Sauvegarde..." : "Enregistrer"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
